import { IReservationsDay } from '@/interfaces/IReservation';
import {
  groupDayReservations,
  isDimmedReservation,
} from './reservation-day-grouping';
import { formatDateLong, formatTime } from './reservation-time';

export type IReservationPrintOp =
  | { kind: 'title' | 'bold' | 'line'; text: string }
  | { kind: 'kvSpace'; left: string; right: string }
  | { kind: 'separator' | 'newline' };
export interface IReservationsPrintInput {
  day: IReservationsDay;
  restaurantName: string;
  branchName: string;
  timeZone: string;
  locale?: string;
  t: (key: string, values?: Record<string, string | number>) => string;
  formatMoney: (amount: number) => string;
  nameWidth?: number;
}

export function buildReservationsDayPrintOps(
  input: IReservationsPrintInput
): IReservationPrintOp[] {
  const label = (key: string, values?: Record<string, string | number>) =>
    input.t(`reservations.print.${key}`, values);
  const rows = input.day.reservations.filter(
    (value) => !isDimmedReservation(value)
  );
  const groups = groupDayReservations(
    rows,
    input.day.shifts,
    input.timeZone
  ).filter((value) => value.reservations.length);
  const ops: IReservationPrintOp[] = [
    { kind: 'title', text: label('title') },
    {
      kind: 'line',
      text: `${label('store')}: ${input.restaurantName} · ${label('branch')}: ${
        input.branchName
      }`,
    },
    {
      kind: 'line',
      text: `${label('date')}: ${formatDateLong(
        input.day.date,
        input.timeZone,
        input.locale
      )}`,
    },
    { kind: 'separator' },
  ];
  for (const group of groups) {
    const name = group.shift
      ? `${group.shift.name ?? label('shift')} ${group.shift.from.slice(
          0,
          5
        )}-${group.shift.to.slice(0, 5)}`
      : input.t('reservations.list.noShift');
    ops.push({
      kind: 'bold',
      text: `${name} · ${label('summary', {
        count: group.count,
        covers: group.covers,
      })}`,
    });
    for (const row of group.reservations) {
      const guest = Array.from(row.guestName ?? '')
        .slice(0, input.nameWidth ?? 22)
        .join('');
      const walkIn = row.source === 'walk_in' ? ` (${label('walkIn')})` : '';
      const deposit =
        row.depositAmount &&
        ['paid', 'applied', 'refund_failed'].includes(row.depositStatus)
          ? ` ${label('deposit')} ${input.formatMoney(row.depositAmount)}`
          : '';
      ops.push({
        kind: 'kvSpace',
        left: `${formatTime(row.startsAt, input.timeZone)}  ${guest}${walkIn}`,
        right: `${row.partySize}${label('people')}  ${row.tables
          .map((table) => table.name)
          .join(' + ')}${deposit}`,
      });
      const notes = [row.notesGuest, row.notesInternal].filter(
        (note): note is string => !!note
      );
      if (notes.length)
        ops.push({
          kind: 'line',
          text: `       ${label('note')}: ${[...new Set(notes)].join(' · ')}`,
        });
    }
    ops.push({ kind: 'separator' });
  }
  if (!rows.length) ops.push({ kind: 'line', text: label('none') });
  ops.push(
    {
      kind: 'bold',
      text: label('total', {
        count: rows.length,
        covers: rows.reduce((sum, row) => sum + row.partySize, 0),
      }),
    },
    { kind: 'newline' }
  );
  return ops;
}
